import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProcessRemoteControllerService } from 'core/api';
import { ConfigService } from 'core/config';
import { EProcessStatus, ESigningOrderType, IPDFDocumentDto, IProcessDto, IWatcherDto } from 'core/dto';
import { filterAllTruthyInTuple, mapKey, subjectFromObs } from 'core/rx-js';
import { LoaderService, MessageBoxService, ScreenUtilsService } from 'core/services';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';
import { designerPaths } from '../../designer.paths';
import { UserService } from '../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { SidenavService } from '../../root/sidenav/sidenav.service';
import { IActivity } from './dto-map/i-activity';
import { ITask } from './dto-map/i-task';
import { ISplugParametersConfiguration } from './remotes/remote-splug-parameters-configuration.service';


@Injectable()
export class SignProcessWizardService {
  private readonly _destroy = new Subject<void>();

  //process defs

  footer: HTMLElement
  header: HTMLElement
  stepper: HTMLElement
  processStatus: EProcessStatus

  get otherElements() {
    return [this.header, this.footer]
  }
  get baseElement() {
    return this.stepper
  }



  readonly processSubject = new BehaviorSubject<IProcessDto>(undefined)
  readonly processIdObs = this.processSubject.pipe(
    filter(Boolean),
    map(({ id }) => id!)
  );

  readonly processIdSubject = new BehaviorSubject<string>(undefined);
  get #processId() {
    return this.processIdSubject.value
  }

  get #defaultSignOrderType() {
    const { signingOrderTypeDefault } = this._configService.appConfig;
    if (signingOrderTypeDefault === 'custom') {
      return ESigningOrderType.custom
    }

    if (signingOrderTypeDefault === 'paralel') {
      return ESigningOrderType.paralel
    }

    if (signingOrderTypeDefault === 'serial') {
      return ESigningOrderType.serial
    }
  }
  readonly processSigningOrderSubject = new BehaviorSubject<ESigningOrderType>(this.#defaultSignOrderType);
  get #processSigningOrder() {
    return this.processSigningOrderSubject.value
  }

  readonly processInfoSubject = new BehaviorSubject<Partial<Pick<IProcessDto, 'description' | 'validTill' | 'note' | 'processSharingType' | 'signingOrderType'>>>(undefined);
  get #processInfo() {
    return this.processInfoSubject.value;
  }

  readonly documentUploadStepValidSubject = new BehaviorSubject<boolean>(false)
  readonly selectSignersStepValidSubject = new BehaviorSubject<boolean>(false)
  readonly setSignaturesStepValidSubject = new BehaviorSubject<boolean>(false)

  readonly splugParametersConfigurationsSubject = new BehaviorSubject<ISplugParametersConfiguration[]>([])
  readonly pdfDocumentsSubject = new BehaviorSubject<IPDFDocumentDto[]>([])
  readonly watchersSubject = new BehaviorSubject<IWatcherDto[]>([])
  readonly activitiesSubject = new BehaviorSubject<IActivity[]>([])
  readonly tasksSubject = new BehaviorSubject<ITask[]>([])



  readonly screenSizeSubject = this._screenUtilsService.screenSizeSubject

  readonly meOnlySignerSubject = subjectFromObs({
    initialValue: false,
    observable: combineLatest([
      this._userService.userSubject,
      this.activitiesSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        map(([user, activities]) => activities.length === 1 && activities[0].actor?.email === user.email),
        distinctUntilChanged()
      )
  }
  )
  get meOnlySigner() {
    return this.meOnlySignerSubject.value
  }


  readonly finishDisplayObs = this.meOnlySignerSubject.pipe(
    map((meOnlySigner) => meOnlySigner ? 'ss-actions.sign' : 'ss-process-wizard.finish')
  )

  constructor(
    private readonly _processRemoteControllerService: ProcessRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _router: Router,
    private readonly _userService: UserService,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _sidenavService: SidenavService,
    private readonly _messageBoxService: MessageBoxService<DsgDeepTranslationKey>,
    private readonly _configService: ConfigService
  ) {


    this.processSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      filter((process) => process.status !== EProcessStatus.Draft)
    )
      .subscribe((process) => {
        this._router.navigate([designerPaths.processOverviewDetail, process.id])
      })


    this.screenSizeSubject.pipe(
      map(screenSize => !['xl', 'lg'].includes(screenSize)),
      distinctUntilChanged(),
      filter(smallScreen => smallScreen),
      takeUntil(this._destroy)
    )
      .subscribe(() => {
        this._sidenavService.expandedSubject.next(false);
      })

    this.#boundProcessInfoToSigningType();


  }

  #boundProcessInfoToSigningType() {

    this.processSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      mapKey('signingOrderType'),
      filter((signingOrderType) => signingOrderType !== this.#processSigningOrder)
    )
      .subscribe((signingOrderType) => {
        this.processSigningOrderSubject.next(signingOrderType)
      })

    this.processInfoSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      filter(processInfo => processInfo.signingOrderType && processInfo.signingOrderType !== this.#processSigningOrder)
    )
      .subscribe(({ signingOrderType }) => {


        this.processSigningOrderSubject.next(signingOrderType)
      })


    this.processSigningOrderSubject.pipe(

      takeUntil(this._destroy),
      filter((processSigningOrder) => processSigningOrder !== this.#processInfo?.signingOrderType)
    )
      .subscribe((processSigningOrder) => {

        this.processInfoSubject.next({ ...(this.#processInfo || {}), signingOrderType: processSigningOrder })
      })
  }

  finishProcess() {

    const id = this._loaderService.show({ text: 'ss-process-wizard.finishing' })
    this._processRemoteControllerService.finishProcess(this.processSubject.value.id)
      .pipe(
        take(1),
        this._loaderService.disposeLoader(id)
      )
      .subscribe(() => {

        if (this.meOnlySigner) {
          const firstActivity = this.activitiesSubject.value[0];

          window.open(window.location.origin + '/viewer/' + firstActivity.id);
          this._router.navigate([''])

        }

        this._router.navigate([''])
      })

  }

  back() {


    if (this.processIdSubject.value) {
      this._messageBoxService.show({
        buttons: 'Ok',
        type: 'info',
        title: 'ss-process-wizard.process-back.display',
        text: 'ss-process-wizard.process-back.message'

      })
        .pipe(
          take(1)
        )
        .subscribe((answer) => {
          if (!answer) {
            return;
          }

          if (this.processStatus) {
            this._router.navigate([designerPaths.processOverview, this.processStatus]);
            return;
          }

          this._router.navigate([designerPaths.processOverview]);
        })


      return;
    }


    if (this.processStatus) {
      this._router.navigate([designerPaths.processOverview, this.processStatus]);
      return;
    }

    this._router.navigate([designerPaths.processOverview]);


  }

  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();

    this.processIdSubject.complete();
    this.documentUploadStepValidSubject.complete();
    this.selectSignersStepValidSubject.complete();
    this.setSignaturesStepValidSubject.complete();
    this.pdfDocumentsSubject.complete();
    this.watchersSubject.complete();
    this.processSubject.complete();
    this.splugParametersConfigurationsSubject.complete();
    this.activitiesSubject.complete();
    this.tasksSubject.complete();

  }
}
